import { extend, forOwn, get, has, isNumber, omit } from "lodash-es";
import { UNITS, UNIT_VAL } from "./UnitMapping";

/**
 * Convert between different units
 *
 * @param {Number} magnitude
 * @param {String} fromUnit
 * @param {String} toUnit (Optional) if not then convert to root unit
 */
function convert(magnitude, fromUnit, toUnit) {
	if (toUnit && !isCompatible(fromUnit, toUnit)) {
		throw new Error(`incompatible unit conversion${fromUnit} ${toUnit}`);
	}

	const fromValue = get(UNIT_VAL, fromUnit);
	const toValue = get(UNIT_VAL, toUnit, 1);

	return magnitude * (fromValue / toValue);
}

/**
 * Checks the compatibility between two units
 *
 * @param {String} unit1
 * @param {String} unit2
 */
function isCompatible(unit1, unit2) {
	if (!unit1 || !unit2) {
		return false;
	}

	const parent1 = unit1.replace(/(.*)\..*$/, "$1");
	const parent2 = unit2.replace(/(.*)\..*$/, "$1");

	return parent1 == parent2;
}

function highestUnit(units) {
	const unitClass = units[0].replace(/(.*)\..*$/, "$1");
	let highestUnit_;
	let highestUnitValue;

	for (const unit of units) {
		const unitSuffix = unit.replace(/.*?\.(.*)/, "$1");
		forOwn(UNIT_VAL[unitClass], (v, u) => {
			if (!(u === unitSuffix)) {
				return;
			}
			if (highestUnitValue == null || highestUnitValue < v) {
				highestUnitValue = v;
				highestUnit_ = unit;
			}
		});
	}

	return highestUnit_;
}

/**
 * normalize the magnitude to nearest unit in the class.
 *
 * @param {String} unit source unit
 * @param {Number} magnitude value
 */
function normalize(magnitude, unit) {
	if (magnitude == 0) {
		const unitDetail = unitDisplayDetail(unit);
		return {
			unitSymbol: unitDetail.ABBRIVIATION,
			unit,
			magnitude: 0
		};
	}
	const baseMagnitude = convert(magnitude, unit);
	const unitClass = unit.replace(/(.*)\..*$/, "$1");
	let unitSuffix = unit.replace(/.*?(\/.*)/, "$1");
	unitSuffix = unitSuffix == unit ? "" : unitSuffix;

	let minValue = baseMagnitude,
		minUnit,
		baseUnit;
	let unitValues = UNIT_VAL[unitClass];

	// Storage class - Bit and Byte seperation
	if (unitClass.startsWith("STORAGE")) {
		const filteredUnitValues = {};
		// source is bit or byte
		const bB = unit.replace(/^[A-Z_]+\..*?([bB]).*$/, "$1");
		forOwn(unitValues, (v, u) => {
			if (u.includes(bB)) {
				filteredUnitValues[u] = v;
				if (!baseUnit || unitValues[baseUnit] > v) {
					baseUnit = u;
				}
			}
		});
		minValue = convert(magnitude, unit, `${unitClass}.${baseUnit}`);
		unitValues = filteredUnitValues;
	}

	// Find the unit with which, magnitude is minimum
	// while being >= 1 (not 0.999)
	// [default to base unit]
	forOwn(unitValues, (v, u) => {
		if (v == 1) {
			baseUnit = u;
		}

		if (!u.endsWith(unitSuffix)) {
			return;
		}

		const computedValue = baseMagnitude / v;
		if (computedValue >= 1 && (computedValue <= minValue || minValue < 1)) {
			minValue = computedValue;
			minUnit = u;
		}
	});

	minUnit = minUnit || baseUnit;
	return {
		unitSymbol: minUnit,
		unit: `${unitClass}.${minUnit}`,
		magnitude: minValue
	};
}

/**
 * format a number by appending unit symbol
 *
 * @param {String} unit Unit identifier
 * @param {Number} magnitude Numeric value
 * @param {Object} options (optional) edit configuration
 */
function format(magnitude, unit, options) {
	// debugger;
	//console.log("format ", magnitude, unit);
	options = extend({ normalize: true, toFixed: "auto", space: false }, options);

	if (options.normalize) {
		const n = normalize(magnitude, unit);
		unit = n.unit;
		magnitude = n.magnitude;
	}

	if (options.toFixed == "auto" && magnitude % 1 !== 0) {
		magnitude = magnitude.toFixed(2);
	}
	if (isNumber(options.toFixed)) {
		magnitude = magnitude.toFixed(options.toFixed);
	}

	const display = unitDisplayDetail(unit);

	if (display.ABBRIVIATION == "") {
		return `${magnitude}`;
	}

	if (display.BEFORE_NUMBER) {
		return display.ABBRIVIATION + (options.space ? " " : "") + magnitude;
	}

	if (!display.BEFORE_NUMBER) {
		return magnitude + (options.space ? " " : "") + display.ABBRIVIATION;
	}
}
/**
 * format a number by appending unit symbol
 *
 * @param {String} fromUnit from Unit
 * @param {String} toUnit to Unit
 * @param {Number} magnitude Numeric value
 * @param {Object} options (optional) edit configuration
 */
function convertAndFormat(magnitude, fromUnit, toUnit, options) {
	// debugger;
	//console.log("format ", magnitude, unit);
	options = extend({ toFixed: "auto", space: false }, options);

	// if (options.normalize) {
	// 	let n = normalize(magnitude, fromUnit);
	// 	fromUnit = n.unit;
	// 	magnitude = n.magnitude;
	// } else {
	magnitude = convert(magnitude, fromUnit, toUnit);
	fromUnit = toUnit;
	// }

	if (options.toFixed == "auto" && magnitude % 1 !== 0) {
		magnitude = magnitude.toFixed(2);
	}
	if (isNumber(options.toFixed)) {
		magnitude = magnitude.toFixed(options.toFixed);
	}
	if (`${magnitude}` === "-0") {
		magnitude = 0;
	}
	const display = unitDisplayDetail(fromUnit);

	if (display.ABBRIVIATION == "") {
		return `${magnitude}`;
	}

	if (display.BEFORE_NUMBER) {
		return display.ABBRIVIATION + (options.space ? " " : "") + magnitude;
	}

	if (!display.BEFORE_NUMBER) {
		return magnitude + (options.space ? " " : "") + display.ABBRIVIATION;
	}
}

// /**
//  * Method to return the highest uit available in all metrics from a visualization
//  * @param {Object} sources Object of sources with metrics per source
//  * @param {*} visualization Object of visualization to which the metrics belong
//  */
// let findHighestUnit = (sources, widget) => {
// 	let highestAvailableUnit = "";
// 	let metricUnitWisePreviewData = [];
// 	let currentUnitClass = undefined;
// 	let isDifferentUnit = false;
// 	Object.keys(sources).forEach(sourceId => {
// 		Object.keys(sources[sourceId]).forEach(metricId => {
// 			let metric = widget.metrics.find(metric => metricId == metric.metricId);
// 			let unitClass = metric && metric.unit ? metric.unit.split(".")[0] : "";
// 			if (currentUnitClass === undefined) currentUnitClass = unitClass;
// 			if (currentUnitClass !== unitClass) isDifferentUnit = true;
// 			metricUnitWisePreviewData.push({
// 				metricUnit: metric ? metric.unit : "",
// 				previewData: sources[sourceId][metricId]
// 			});
// 		});
// 	});
// 	if (isDifferentUnit === true) return null;

// 	({ unit: highestAvailableUnit } = getHighestUnitFromPreviewData(metricUnitWisePreviewData));
// 	return highestAvailableUnit;
// };

/**
 * Get the highest Unit if comman metrics exists &
 * normalize the data on the bases of that highest unit.
 */
const sanitizeDataPoints = ({ sources, highestAvailableUnit, showCommonMetricsymbol, widget }) => {
	const metricDataPoints = {};
	let highestUnitPerMetric = {};
	let normalizedUnitData = {};
	Object.keys(sources).forEach(sourceId => {
		Object.keys(sources[sourceId]).forEach(metricId => {
			const metricUniqueId = `${sourceId}-${metricId}`;
			const metric = widget.metrics.find(metric_ => metricId == metric_.metricId);
			const metricUnit = metric ? metric.unit : "";
			highestUnitPerMetric = {
				[`${metricId}`]: highestAvailableUnit
			};
			normalizedUnitData = {
				[`${metricId}`]: {}
			};

			let datapointArray = [];

			sources[sourceId][metricId].forEach(({ timestamp, data }) => {
				const roundOffTimestamp = new Date(timestamp).getTime();
				let dataPoint = data;
				let { magnitude, unit, unitSymbol } = normalize(data, metricUnit);
				normalizedUnitData[`${metricId}`] = { unitSymbol, unit };

				if (metricUnit && showCommonMetricsymbol && highestUnitPerMetric[metricId]) {
					magnitude = convert(data, metricUnit, highestUnitPerMetric[metricId]);
					dataPoint = magnitude;
				} else if (metricUnit && !showCommonMetricsymbol) {
					dataPoint = magnitude;
				}

				datapointArray.push({
					timestamp: roundOffTimestamp,
					value: dataPoint
				});
				// eslint-disable-next-line max-nested-callbacks
				datapointArray = datapointArray.sort((a, b) => a - b);

				metricDataPoints[metricUniqueId] = [...datapointArray];
			});
		});
	});
	return {
		metricDataPoints,
		highestAvailableUnit,
		showCommonMetricsymbol,
		highestUnitPerMetric,
		normalizedUnitData
	};
};

// // It required to process the source data points
// /**
//  * return values
//  * 1. unitSymbol ( this is sitting in timeseriesWidgetMixin)
//  * 2. metricDataPoints ( this is sitting in wrapperWidgettimeseries)
//  */
// let processSourceData = (timeseriesData, widget) => {
// 	let highestAvailableUnit = "";
// 	let showCommonMetricsymbol = "";
// 	let unitSymbol = "";
// 	const sources = timeseriesData;

// 	// Find the highest unit available in a widget for all the metrics
// 	let currentHighestUnit = findHighestUnit(sources, widget);
// 	if (currentHighestUnit === null) {
// 		return;
// 	}
// 	// pull the old unit from widget data.
// 	let oldHighestUnit = widget && widget.metaData.unit ? widget.metaData.unit : "";

// 	// Initialize the highest available unit in comparison to currentHighestUnit
// 	highestAvailableUnit =
// 		oldHighestUnit && oldHighestUnit != currentHighestUnit ? currentHighestUnit : oldHighestUnit;

// 	if (highestAvailableUnit) {
// 		// Code to pull unit symbol and its abbreviation
// 		let highestUnitSplitArray = highestAvailableUnit.split(".");
// 		unitSymbol = highestUnitSplitArray.length > 1 ? highestUnitSplitArray[1] : "";
// 		showCommonMetricsymbol = highestUnitSplitArray.length ? highestUnitSplitArray[0] : "";
// 	}

// 	let data = sanitizeDataPoints({
// 		sources,
// 		highestAvailableUnit,
// 		showCommonMetricsymbol,
// 		widget: widget
// 	});

// 	return { data, unitSymbol };
// };
/**
 * Find unit class
 *
 * @param {String} symbol
 */
function findUnitFromSymbol(symbol) {
	const unitClasses = [];
	forOwn(UNIT_VAL, (unitClass, unitClassName) => {
		if (has(unitClass, symbol)) {
			unitClasses.push(unitClassName);
		}
	});

	if (unitClasses.length > 1 || unitClasses.length < 1) {
		throw new Error("Unit class not found or ambiguous result");
	}

	return {
		unitClass: unitClasses[0],
		unit: `${unitClasses[0]}.${symbol}`
	};
}

/**
 * Return display details of a unit
 *
 * @param {String} unit
 */
function unitDisplayDetail(unit) {
	const [parent, symbol] = unit.split(".");
	const parentDisplayDefault = get(UNITS, `['${parent}'].DEFAULT`);
	const unitDisplayDetails = get(UNITS, `['${parent}'].UNITS['${symbol}']`);
	return extend({}, parentDisplayDefault, omit(unitDisplayDetails, "VALUE"));
}

function getAllUnitsFromUnitClass(unit) {
	return get(UNITS, `['${unit}'].UNITS`);
}

export default {
	convert,
	isCompatible,
	format,
	convertAndFormat,
	normalize,
	findUnitFromSymbol,
	unitDisplayDetail,
	highestUnit,
	getAllUnitsFromUnitClass,
	// processSourceData,
	// findHighestUnit,
	sanitizeDataPoints
};
